import { useEffect, useState } from "react";

import { useReactiveTimeout } from "client-util/useReactiveTimeout";
import { ScopeType } from "sockapi/scope-types";
import { AdminQueueTaskSummary } from "sockapi/scope-specs/admin-queue-spec";
import { SockClient } from "sockapi/client/SockClient";
import { useSockClient } from "stores/ServerInteractionStore";
import { useScopeStateResolvable } from "stores/ScopeStateStore";
import { useNavStore } from "stores/NavStore";
import { useAdminPreferencesStore } from "stores/UserPreferencesStore";
import { Spinner } from "components/Loading";
import { Link } from "components/Link";
import { LongDate } from "components/dates";
import Modal from "components/Modal";
import Tooltip from "components/Tooltip";

/** Returns whether the claim was successful. */
export const sendAdminQueueClaimStepAsync = async (
  sockClient: SockClient,
  taskId: string,
  staffId: string
): Promise<boolean> => {
  const isSuccessful = await sockClient.sendScopeStepAndWaitAsync(
    {
      type: ScopeType.ADMIN_QUEUE_TASK,
      taskId,
    },
    {
      claim: { staffId },
    }
  );
  if (isSuccessful === null) return false;
  if (!isSuccessful) toastr.error("Someone claimed before you!");
  return isSuccessful;
};

function ClaimedBy(props: {
  taskId: string;
  taskSummary: AdminQueueTaskSummary;
  useClaimLink?: true;
}) {
  const { taskId, taskSummary, useClaimLink } = props;
  const adminQueueTaskScopeState = useScopeStateResolvable(
    ScopeType.ADMIN_QUEUE_TASK,
    {
      taskId,
    }
  );
  const staffId = useAdminPreferencesStore((state) => state.staffId);
  const sockClient = useSockClient();
  const setRedirectRoute = useNavStore((state) => state.setRedirectRoute);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (staffId === null)
      setRedirectRoute({ route: "admin_queue", replace: true });
  }, [staffId, setRedirectRoute]);

  const hasUnsoonzed = useReactiveTimeout(
    adminQueueTaskScopeState?.snooze?.snoozeUntil ?? null
  );

  if (adminQueueTaskScopeState === null || staffId === null)
    return <Spinner inline={true} />;
  const { claim, snooze } = adminQueueTaskScopeState;
  const isClaimedBySelf = claim !== undefined && claim.staffId === staffId;
  const isSnoozed = snooze !== undefined && !hasUnsoonzed;

  const onUnclaim = async () => {
    await sockClient.sendScopeStepAndWaitAsync(
      {
        type: ScopeType.ADMIN_QUEUE_TASK,
        taskId,
      },
      {
        unclaim: true,
      }
    );
    if (isClaimedBySelf) setRedirectRoute({ route: "admin_queue" });
  };

  return (
    <div className="admin-queue-claimed-by">
      {claim === undefined && !isSnoozed ? (
        <div className="admin-queue-claim-button button-squish-zone">
          {(useClaimLink ?? false) ? (
            <Link route="admin_queue_claim" params={{ taskId }} button={true}>
              Claim
            </Link>
          ) : (
            <button
              onClick={() => {
                (async () => {
                  await sendAdminQueueClaimStepAsync(
                    sockClient,
                    taskId,
                    staffId
                  );
                })().catch(console.error);
              }}
            >
              Claim
            </button>
          )}
          <Tooltip text="Snooze for 10 minutes">
            <button
              onClick={() => {
                (async () => {
                  await sockClient.sendScopeStepAndWaitAsync(
                    { type: ScopeType.ADMIN_QUEUE_TASK, taskId },
                    { snooze: { staffId, snoozeFor: 10 * 60 * 1000 } }
                  );
                  toastr.success("Snoozed for 10 minutes");
                })().catch(console.error);
              }}
            >
              Snooze
            </button>
          </Tooltip>
        </div>
      ) : claim !== undefined ? (
        <>
          <div className="admin-queue-claim-button">
            <button
              onClick={() => {
                if (isClaimedBySelf) {
                  onUnclaim().catch(console.error);
                } else {
                  setShowModal(true);
                }
              }}
            >
              {isClaimedBySelf ? "Unclaim" : "Kick"}
            </button>
          </div>
          <div className="admin-queue-task-metadata">
            <div>Claimed by: {claim.staffId}</div>
            <div>
              at <LongDate timestamp={claim.claimTime} />
            </div>
          </div>
        </>
      ) : snooze !== undefined ? (
        <>
          <div className="admin-queue-claim-button">
            <button
              onClick={() => {
                (async () => {
                  await sockClient.sendScopeStepAndWaitAsync(
                    { type: ScopeType.ADMIN_QUEUE_TASK, taskId },
                    { unsnooze: true }
                  );
                })().catch(console.error);
              }}
            >
              Unsnooze
            </button>
          </div>
          <div className="admin-queue-task-metadata">
            <div>Snoozed by: {snooze.staffId}</div>
            <div>
              until <LongDate timestamp={snooze.snoozeUntil} />
            </div>
          </div>
        </>
      ) : null}
      <div>
        <div className="admin-queue-task-metadata">
          Opened: <LongDate timestamp={taskSummary.requestTime} />
        </div>
        {taskSummary.respondTime !== undefined ? (
          <div className="admin-queue-task-metadata">
            Closed: <LongDate timestamp={taskSummary.respondTime} />
          </div>
        ) : null}
      </div>
      <Modal
        isOpen={showModal}
        title={`Are you sure you want to kick ${claim?.staffId} from this task?`}
        footerChildren={
          <>
            <button onClick={() => setShowModal(false)}>Cancel</button>
            <button
              onClick={() => {
                onUnclaim()
                  .then(() => setShowModal(false))
                  .catch(console.error);
              }}
            >
              Kick
            </button>
          </>
        }
      />
    </div>
  );
}

export default ClaimedBy;
